import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * Animates a product image from the Add to Basket button
 * towards the visible cart icon.
 */
class FlyToCart extends Component {
  /** @type {Element | null} */
  source = null;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element | null} */
  destination = null;

  connectedCallback() {
    super.connectedCallback();

    requestAnimationFrame(() => {
      const source = this.source;
      const destination = this.#findBestCartDestination();

      if (!(source instanceof Element) || !(destination instanceof Element)) {
        this.remove();
        return;
      }

      const sourceRect = source.getBoundingClientRect();
      const destinationRect = destination.getBoundingClientRect();

      if (!this.#hasUsableSize(sourceRect)) {
        this.remove();
        return;
      }

      this.#animate(sourceRect, destinationRect);
    });
  }

  /**
   * Finds the cart icon that is currently visible.
   * This avoids targeting hidden desktop/mobile duplicates.
   *
   * @returns {Element | null}
   */
  #findBestCartDestination() {
    const cartIcons = Array.from(
      document.querySelectorAll('.header-actions__cart-icon')
    );

    const visibleIcon = cartIcons.find((icon) => {
      const rect = icon.getBoundingClientRect();
      const styles = window.getComputedStyle(icon);

      const isRendered =
        rect.width > 0 &&
        rect.height > 0 &&
        styles.display !== 'none' &&
        styles.visibility !== 'hidden' &&
        styles.opacity !== '0';

      const isInsideViewport =
        rect.bottom > 0 &&
        rect.top < window.innerHeight &&
        rect.right > 0 &&
        rect.left < window.innerWidth;

      return isRendered && isInsideViewport;
    });

    if (visibleIcon) {
      return visibleIcon;
    }

    const renderedIcon = cartIcons.find((icon) => {
      const rect = icon.getBoundingClientRect();
      const styles = window.getComputedStyle(icon);

      return (
        rect.width > 0 &&
        rect.height > 0 &&
        styles.display !== 'none' &&
        styles.visibility !== 'hidden'
      );
    });

    return renderedIcon || this.destination;
  }

  /**
   * @param {DOMRect | DOMRectReadOnly} rect
   */
  #hasUsableSize(rect) {
    return rect.width > 0 && rect.height > 0;
  }

  /**
   * Returns a desktop-only animation speed based on travel distance.
   * Mobile remains completely unchanged.
   *
   * @param {number} distance
   * @returns {number}
   */
  #getDesktopPlaybackRate(distance) {
    /*
     * Products close to the cart use a slightly quicker animation,
     * preventing short journeys from looking slow or laggy.
     */
    if (distance < 300) {
      return 1.3;
    }

    if (distance < 600) {
      return 1.18;
    }

    if (distance < 900) {
      return 1.08;
    }

    return 1;
  }

  /**
   * Animates the product towards the cart.
   *
   * @param {DOMRect | DOMRectReadOnly} sourceRect
   * @param {DOMRect | DOMRectReadOnly} destinationRect
   */
  #animate = async (sourceRect, destinationRect) => {
    const viewportPadding = 24;

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    let destinationX =
      destinationRect.left + destinationRect.width / 2;

    let destinationY =
      destinationRect.top + destinationRect.height / 2;

    const destinationIsOutsideViewport =
      destinationRect.bottom <= 0 ||
      destinationRect.top >= window.innerHeight ||
      destinationRect.right <= 0 ||
      destinationRect.left >= window.innerWidth;

    /*
     * When the header has scrolled outside the viewport, keep the
     * animation visible by sending it towards the nearest screen edge.
     */
    if (destinationIsOutsideViewport) {
      destinationX = Math.min(
        Math.max(destinationX, viewportPadding),
        window.innerWidth - viewportPadding
      );

      destinationY = Math.min(
        Math.max(destinationY, viewportPadding),
        window.innerHeight - viewportPadding
      );
    }

    const travelX = destinationX - startPoint.x;
    const travelY = destinationY - startPoint.y;
    const travelDistance = Math.hypot(travelX, travelY);

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }

    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${travelX}px`);
    this.style.setProperty('--travel-y', `${travelY}px`);

    await yieldToMainThread();

    const animations = this.getAnimations();
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;

    /*
     * Leave mobile timing exactly as defined by the theme.
     * Only make short and medium desktop flights slightly quicker.
     */
    if (isDesktop) {
      const playbackRate =
        this.#getDesktopPlaybackRate(travelDistance);

      animations.forEach((animation) => {
        if (typeof animation.updatePlaybackRate === 'function') {
          animation.updatePlaybackRate(playbackRate);
        } else {
          animation.playbackRate = playbackRate;
        }
      });
    }

    if (animations.length > 0) {
      await Promise.allSettled(
        animations.map((animation) => animation.finished)
      );
    }

    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}